import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import type {Column} from '../../../common/DataTable/DataTable'
import DataTable from '../../../common/DataTable/DataTable'
import {Button} from 'pepsico-ds'
import {dashboardService} from '@/services/common'
import useApiRequest from '@/hooks/useApiRequest'
import {clearCacheForEndpoint} from '@/services/interceptors/cacheInterceptor'
import {ENDPOINTS} from '@/utils/constants/apiEndpoints'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import ModalWrapper from '@/components/GDS/molecules/Modal/ModalWrapper'
import {useDispatch, useSelector} from 'react-redux'
import type {RootState} from '@/store'
import {setOnboardingDashboardInfo} from '@/store/onboardingSlices'
import {resetAllOnboardingSlices} from '@/store/resetOnboardingSlices'
import TeamOnboardingFlow from '@/components/SelfOnboarding/TeamsDashboard/TeamOnboardingFlow'
import {useFeatureFlag} from '@/contexts/GrowthBookContext'
import Toast from '@/components/GDS/molecules/Toast'
import {
  TOAST_MESSAGES,
  MODAL_MESSAGES,
  TABLE_CONFIG,
  TABLE_HEADERS,
  BUTTON_LABELS,
  SEARCH_PLACEHOLDERS,
  API_CONFIG,
} from '../constants'
import {createToast} from '../helpers/toastHelpers'

// Define the dashboard data type
interface Dashboard {
  id: string
  name: string
  type: 'Program' | 'Team'
  owner: string
  createdDate: string
  lastModified: string
  status: 'active' | 'inactive' | 'draft'
}

const ManageDashboards: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const enableTeamOnboardingV2 = useFeatureFlag('enable_team_onboarding_v2') ?? true
  // Get onboardingDashboard from Redux store

  const onboardingDashboard = useSelector((state: RootState) => state.onboardingDashboard)
  const [searchTerm, setSearchTerm] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState<number>(TABLE_CONFIG.DEFAULT_PAGE_SIZE)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)

  // State for onboarding modal
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [toasts, setToasts] = useState<any[]>([])
  const [showToast, setShowToast] = useState(false)

  // Create stable params for dashboard API with pagination and search support
  const dashboardParams = React.useMemo(() => {
    const params = {
      page: pageIndex, // Already 0-indexed for API
      size: pageSize, // Use dynamic page size
      search: searchTerm,
      ...(sortBy && {sort: sortBy}), // Only include sort if selected
    }

    return params
  }, [pageIndex, pageSize, searchTerm, sortBy])

  // Add local loading state to handle pagination loading
  const [isPaginationLoading, setIsPaginationLoading] = useState(false)

  // Fetch dashboard data using the same API as LandingPage
  const {
    data: dashboardData,
    isLoading: isApiLoading,
    refetch,
  } = useApiRequest(dashboardService.getTeamsList, dashboardParams)

  // Combine API loading state with pagination loading state
  const isLoading = isApiLoading || isPaginationLoading

  // Process dashboard data when it's available
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [totalCount, setTotalCount] = useState(100) // Start with a default value until we get the real count

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [dashboardToDelete, setDashboardToDelete] = useState<string | null>(null)
  const [dashboardNameToDelete, setDashboardNameToDelete] = useState<string>('')

  // Reset loading state when data is received
  useEffect(() => {
    if (dashboardData) {
      // Clear pagination loading state when data is received
      setIsPaginationLoading(false)
    }
  }, [dashboardData, pageIndex, pageSize])

  // Format dashboards data for the table
  useEffect(() => {
    if (dashboardData) {
      // Exact API response format: { dashboards: [], page: 0, size: 10, total: 261 }
      let dashboardsArray

      // The response should be a DashboardPageResponse with content property
      // See types.ts: DashboardPageResponse { content: DashboardItem[], totalElements: number, ... }
      if ((dashboardData as any)?.content && Array.isArray((dashboardData as any).content)) {
        dashboardsArray = (dashboardData as any).content
      }
      // Check if the response has the dashboards property as in some API responses
      else if ((dashboardData as any)?.dashboards && Array.isArray((dashboardData as any).dashboards)) {
        dashboardsArray = (dashboardData as any).dashboards
      } else if (Array.isArray(dashboardData)) {
        // Direct array response
        dashboardsArray = dashboardData
      } else {
        // Fallback to empty array
        dashboardsArray = []
      }

      // Map API response to Dashboard interface
      const formattedDashboards = dashboardsArray.map((item: any) => ({
        id: item.id || '',
        name: item.title || item.name || '',
        type: item.properties?.type || 'Team',
        owner: item.properties?.owner || 'Unknown',
        createdDate: new Date(item.createdAt || Date.now()).toISOString().split('T')[0],
        lastModified: new Date(item.updatedAt || Date.now()).toISOString().split('T')[0],
        status: item.properties?.status || 'active',
      }))

      setDashboards(formattedDashboards)

      // Make sure we properly extract the total count from the API response
      if (dashboardData && typeof dashboardData === 'object') {
        // DashboardPageResponse has totalElements property
        if ('totalElements' in dashboardData && typeof dashboardData.totalElements === 'number') {
          setTotalCount(dashboardData.totalElements)
        }
        // Some API responses use total property
        else if ('total' in dashboardData && typeof dashboardData.total === 'number') {
          setTotalCount(dashboardData.total)
        }
        // If we can't find a total count but have a content array, use its length
        else if ('content' in dashboardData && Array.isArray(dashboardData.content)) {
          setTotalCount(dashboardData.content.length)
        }
        // If we can't find a total count but have a dashboards array, use its length
        else if ('dashboards' in dashboardData && Array.isArray(dashboardData.dashboards)) {
          setTotalCount(dashboardData.dashboards.length)
        }
        // If we can't find any of the above, use the formatted dashboards length
        else {
          setTotalCount(formattedDashboards.length)
        }
      } else {
        // Fallback to dashboards length
        setTotalCount(Math.max(261, formattedDashboards.length)) // Use 261 as a minimum (from your example)
      }
    }
  }, [dashboardData])

  // Toast handler for dashboard actions
  const showDashboardToast = (type: 'Team' | 'Program', dashboardName: string, isEdit?: boolean) => {
    const action = isEdit ? 'updated' : 'created'
    setToasts([
      createToast(`${type} Dashboard ${action}`, `${dashboardName} has been successfully ${action}.`, 'success'),
    ])
    setShowToast(true)
  }

  // Handler function to refresh data after edit/delete
  const refreshData = useCallback(() => {
    clearCacheForEndpoint(ENDPOINTS.TEAMS_LIST_PAGE_FILTER)
    setIsPaginationLoading(true)
    refetch()
  }, [refetch])

  // Define columns for the DataTable
  const columns: Column[] = [
    {
      columnKey: 'name',
      header: 'Team Dashboard Name',
      cellData: ({row}) => (
        <div
          className="flex w-full items-center justify-between"
          style={{
            display: 'flex',
            height: '32px',
            minHeight: '32px',
            padding: 'var(--Spacing-spacing-8, 8px) 0',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}
        >
          <div
            className="flex-1 font-[Inter] text-sm leading-5 font-normal text-[var(--color-text-primary)]"
            style={{
              display: 'flex',
              padding: '0 var(--Spacing-spacing-8, 8px)',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '-2px',
              flex: '1 0 0',
            }}
          >
            {row.original.name}
          </div>
          <div
            className="flex space-x-2"
            style={{
              display: 'flex',
              padding: '0 var(--Spacing-spacing-8, 8px)',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '-2px',
            }}
          >
            <Button
              onClick={() => handleEdit(row.original.id)}
              iconLeading="edit"
              variant="tertiary"
              size="medium"
              text="Edit"
            />
            <div
              data-size="128px"
              style={{
                transform: 'rotate(90deg)',
                transformOrigin: 'top left',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 10,
                display: 'inline-flex',
              }}
            >
              <div style={{width: 128, height: 1, background: 'rgba(254.57, 0, 0, 0)'}} />
            </div>
          </div>
          <div
            className="flex space-x-2"
            style={{
              display: 'flex',
              padding: '0 var(--Spacing-spacing-8, 8px)',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '-2px',
            }}
          >
            <Button
              onClick={() => handleDelete(row.original)}
              iconLeading="delete"
              variant="tertiary"
              size="medium"
              text="Remove"
            />
            <div
              data-size="128px"
              style={{
                transform: 'rotate(90deg)',
                transformOrigin: 'top left',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 10,
                display: 'inline-flex',
              }}
            >
              <div style={{width: 128, height: 1, background: 'rgba(254.57, 0, 0, 0)'}} />
            </div>
          </div>
        </div>
      ),
    },
  ]

  // Handler functions for actions
  const handleEdit = (id: string) => {
    // Find the dashboard data to pre-populate the form
    const dashboardToEdit = dashboards.find(dashboard => dashboard.id === id)

    // Reset all onboarding slices first
    resetAllOnboardingSlices(dispatch)

    // Dispatch the dashboard info to the store
    dispatch(
      setOnboardingDashboardInfo({
        dashboardId: id,
        editMode: true,
        // Include any additional data that might be needed
        dashboardName: dashboardToEdit?.name || '',
      }),
    )

    // Route to the current onboarding page when enabled, otherwise fall back to the legacy modal
    if (enableTeamOnboardingV2) {
      navigate(`/team-dashboard/edit?id=${id}&mode=edit`)
    } else {
      setShowOnboarding(true)
    }
  }

  // Show delete confirmation modal
  const handleDelete = (dashboard: any) => {
    // Check if dashboards array exists
    if (!dashboard) {
      return
    }

    if (dashboard) {
      setDashboardToDelete(dashboard.id)
      setDashboardNameToDelete(dashboard.name)
      setShowDeleteModal(true)
    }
  }

  // Confirm delete action
  const confirmDelete = async () => {
    if (dashboardToDelete) {
      try {
        // Call the delete API
        await dashboardService.deleteDashboard(dashboardToDelete)

        // Update local state immediately to remove the deleted dashboard
        setDashboards(prevDashboards => prevDashboards.filter(dashboard => dashboard.id !== dashboardToDelete))

        // Update total count
        setTotalCount(prevCount => prevCount - 1)

        // Close the modal first
        closeDeleteModal()

        // Show success toast notification
        setToasts([
          createToast(
            TOAST_MESSAGES.DASHBOARD_REMOVED,
            TOAST_MESSAGES.DASHBOARD_REMOVED_SUCCESS(dashboardNameToDelete),
            'success',
          ),
        ])
        setShowToast(true)

        // Refresh the dashboard list to get the updated data from the server
        setTimeout(() => {
          refreshData()
        }, API_CONFIG.REFETCH_DELAY)
      } catch (error) {
        // Close the modal
        closeDeleteModal()

        // Show error toast notification
        setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.DASHBOARD_REMOVE_ERROR, 'error')])
        setShowToast(true)
      }
    }
  }

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setDashboardToDelete(null)
    setDashboardNameToDelete('')
  }

  // Handle global search change - this triggers a new API call with search term
  const handleGlobalSearchChange = (value: string) => {
    setIsPaginationLoading(true)
    setSearchTerm(value)
    setPageIndex(0) // Reset to first page on search
  }

  // Handle page change - this triggers a new API call with updated page index
  const handlePageChange = (newPageIndex: number) => {
    // Show loading state
    setIsPaginationLoading(true)

    // The API uses 0-based indexing, and the DataTable component also uses 0-based indexing
    // So we can directly set the page index without adjusting
    setPageIndex(newPageIndex - 1)
  }

  // Handle rows per page change - this triggers a new API call with updated page size
  const handleRowsPerPageChange = (newPageSize: number) => {
    // Show loading state
    setIsPaginationLoading(true)
    // Reset to first page when changing page size
    setTimeout(() => {
      setPageIndex(0)
    }, 100)
    // Update page size
    setPageSize(newPageSize)
  }

  return (
    <div className="w-full p-6">
      <div className="flex w-full flex-1 flex-col items-start gap-4">
        {/* Loading state */}
        {isLoading && searchTerm === '' ? (
          <div className="flex h-screen w-full items-center justify-center">
            <LoadingSpinner size={40} />
          </div>
        ) : (
          <>
            <DataTable
              key={`dashboard-table-${pageSize}`} // Force remount when pageSize changes to reset internal pagination state
              className="w-full [&_tbody_tr]:min-h-[40px]"
              viewPort="Desktop"
              style="naked"
              data={dashboards}
              columns={columns}
              hideHeader={false}
              showSearchOption={true}
              searchPlaceholder={SEARCH_PLACEHOLDERS.DASHBOARDS}
              onGlobalSearchChange={handleGlobalSearchChange}
              debounceDelay={500}
              showDescription={true}
              tableHeader={TABLE_HEADERS.MANAGE_DASHBOARDS}
              tableDescription={TABLE_HEADERS.MANAGE_DASHBOARDS_DESC}
              enableTextSelection={true}
              size="medium"
              showFilterToolbar={false}
              pageSize={pageSize} // Use dynamic page size
              pageIndex={pageIndex} // Current page index (0-based)
              onPageIndexChange={newPageIndex => {
                handlePageChange(newPageIndex)
              }}
              onRowsPerPageChange={(newPageSize: number) => {
                // Only call handler when new page size is different from current
                if (newPageSize !== pageSize) {
                  handleRowsPerPageChange(newPageSize)
                }
              }}
              isLoading={isLoading} // Pass loading state to DataTable
              manualPagination={true} // Enable manual pagination
              totalRowsCount={totalCount} // Total number of rows for pagination calculation
              forceShowPagination={true} // Always show pagination controls
              pageSizeOptions={TABLE_CONFIG.PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>

      {/* Toast notifications */}
      {showToast && (
        <Toast
          toasts={toasts}
          position="bottom-center"
          removeToast={(id: any) => setToasts(prev => prev.filter(t => t.id !== id))}
          showActionIcon
        />
      )}

      {/* Team Onboarding Flow for Edit */}
      {showOnboarding && (
        <TeamOnboardingFlow
          open={showOnboarding}
          onClose={() => {
            setShowOnboarding(false)
            // Refresh on close if in edit mode - step 0 already persisted changes to DB
            if (onboardingDashboard?.editMode) {
              refreshData()
            }
          }}
          onboardingDashboard={onboardingDashboard}
          onToastFinish={dashboardName => {
            setShowOnboarding(false)
            showDashboardToast('Team', dashboardName, onboardingDashboard?.editMode)
            refreshData()
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ModalWrapper
        showModal={showDeleteModal}
        showSlot={true}
        onCloseModal={closeDeleteModal}
        title={MODAL_MESSAGES.DELETE_DASHBOARD_TITLE}
        text=""
        primaryButtonProps={{
          text: BUTTON_LABELS.REMOVE,
          onClick: confirmDelete,
          variant: 'primary',
        }}
        secondaryButtonProps={{
          text: BUTTON_LABELS.CANCEL,
          onClick: closeDeleteModal,
          variant: 'secondary',
        }}
        type="fluid"
      >
        <div className="inline-flex flex-1 items-start justify-start gap-2 self-stretch">
          <div className="inline-flex flex-1 flex-col items-start justify-start gap-0.5 self-stretch pr-2">
            <div className="inline-flex flex-1 items-center justify-start gap-1 self-stretch">
              <div className="flex-1 justify-start self-stretch font-['Inter'] text-base leading-5 font-normal text-[var(--color-text-secondary)]">
                {MODAL_MESSAGES.DELETE_DASHBOARD_CONFIRM_PREFIX} {dashboardNameToDelete}{' '}
                {MODAL_MESSAGES.DELETE_DASHBOARD_CONFIRM_SUFFIX}
                <br />
                <br />
                {MODAL_MESSAGES.DELETE_DASHBOARD_WARNING} {MODAL_MESSAGES.DELETE_DASHBOARD_CANNOT_UNDO}.
              </div>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default ManageDashboards
